import { useCallback, useEffect, useMemo, useState } from "react";
import { queryClient } from "@/client";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  HiOutlineArrowPath,
  HiOutlineArrowRight,
  HiOutlineArrowSmallLeft,
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineMagnifyingGlass,
} from "react-icons/hi2";
import { useParams } from "react-router-dom";

import { useDeleteCompanies } from "@/hooks/api/list/useDeleteCompanies";
import { useGetList } from "@/hooks/api/list/useGetList";
import { listDataQueryKey, useInfiniteListsDetail } from "@/hooks/api/list/useInfiniteListsDetail";
import { useUpdateOrderColumns } from "@/hooks/api/list/useUpdateOrderColumns";
import { useTableConfig } from "@/hooks/table/useTableConfig";
import { useAlert } from "@/hooks/useAlert";
import useDebounceQuery from "@/hooks/useDebounceQuery";
import useTierLimit from "@/hooks/useTierLimit";
import { useToast } from "@/hooks/useToast";
import Badge from "@/components/atoms/Badge";
import Button from "@/components/atoms/Button";
import Input from "@/components/atoms/input/Input";
import Spinner from "@/components/atoms/Spinner";
import CompanyDetailPanel from "@/components/molecules/company/detail/CompanyDetailPanel";
import FeaturedText from "@/components/molecules/FeaturedText";
import ConfigureTableColumnsButton from "@/components/molecules/table/ConfigureTableColumnsButton";
import CRMBadges from "@/components/organisms/crm/CRMBadges";
import CRMModalHandler from "@/components/organisms/crm/CRMModalHandler";
import EmptyListDetail from "@/components/organisms/list/detail/EmptyListDetail";
import ExportListButton from "@/components/organisms/list/ExportListButton";
import ListNotFound from "@/components/organisms/list/ListNotFound";
import NoListResults from "@/components/organisms/list/NoListResults";
import { CompanyListColumns, searchColumns, searchColumnsDef } from "@/components/organisms/search/searchColumns";
import Table from "@/components/organisms/table/Table";
import UnmatchedCompaniesModalHandler from "@/components/organisms/UnmatchedCompaniesModalHandler";

const ListDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { showToast } = useToast();
  const { showAlert } = useAlert();
  const [sidePanelId, setSidePanelId] = useState<string | null>(null);
  const [showPersonColumns, setShowPersonColumns] = useState<boolean>(false);

  const { data: listDetail, isLoading: isLoadingList, isError: listHasError } = useGetList({ id: id! });
  const enrichProgress = useMemo(() => {
    if (!listDetail || !listDetail.enrichment_item_count) return null;
    const counts = listDetail.enrichment_item_count;
    const percentage = Math.round((counts.enriched_enrichment_items / counts.total_enrichment_items) * 100);
    return percentage >= 0 && percentage < 100 ? percentage : null;
  }, [listDetail]);

  //const { updateOrderRows } = useUpdateOrderRows();
  const { updateOrderColumns } = useUpdateOrderColumns();
  const { deleteCompanies } = useDeleteCompanies();

  const { tierLimit } = useTierLimit();

  const { query, setDebounceQuery, isTyping } = useDebounceQuery();
  const {
    data: listDetailTable,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isFetched,
    isFetching,
    isLoading: isLoadingListDetail,
    isError: listDetailHasError,
  } = useInfiniteListsDetail({ id: id!, query });

  const initialLoading = useMemo(() => isLoadingList || isLoadingListDetail, [isLoadingList, isLoadingListDetail]);

  const isStaticList = useMemo(() => listDetail?.type === "STATIC", [listDetail]);

  const flattenedData = useMemo(() => listDetailTable?.pages?.flatMap((page) => page.data) || [], [listDetailTable]);

  const totalResults = useMemo(() => listDetail?.item_count || 0, [listDetail]);
  const loadingRowCount = useMemo(() => (totalResults === 0 ? 25 : Math.min(totalResults, 25)), [totalResults]);

  const unmatchedCount = useMemo(() => listDetail?.unmatched_item_count || 0, [listDetail?.unmatched_item_count]);
  const alertIsShown = useMemo(
    () => listDetail?.show_unmatched_items_notice || false,
    [listDetail?.show_unmatched_items_notice],
  );

  const columnNames = useMemo(() => searchColumns(t), [t]);
  const columnValues = useMemo(() => Object.values(columnNames), [columnNames]);

  /**
   * Computes the initial column order, inserting POC email and LinkedIn columns immediately after
   * the “point_of_contact_full_name” column when showPersonColumns is true.
   */
  const initialColumnOrder = useMemo(
    () =>
      (
        listDetail?.columns
          .slice() // avoid mutating the original
          .sort((a, b) => a.order - b.order) || []
      ).flatMap(({ type }) =>
        type === CompanyListColumns.POINT_OF_CONTACT_FULL_NAME && showPersonColumns
          ? [type, CompanyListColumns.POINT_OF_CONTACT_EMAIL, CompanyListColumns.POINT_OF_CONTACT_LINKEDIN]
          : [type],
      ),
    [listDetail?.columns, showPersonColumns],
  );

  /*const onRowOrderChange = useCallback(
    (rows: string[]) =>
      updateOrderRows({
        listId: id!,
        rows: rows.map((column, index) => ({
          company_id: column,
          order: index,
        })),
      }),
    [id, updateOrderRows],
  );*/

  const onColumnOrderChange = useCallback(
    (newOrder: string[]) =>
      updateOrderColumns({
        listId: id!,
        columnOrder: newOrder.map((column, index) => ({
          type: column,
          order: index,
        })),
      }),
    [id, updateOrderColumns],
  );

  const onRemoveRows = useCallback(
    (rows: string[]) =>
      deleteCompanies(
        { listId: id!, companyIds: rows },
        {
          onSuccess: () => {
            tableConfig.clearRowSelection();
            showToast({
              featuredIcon: { icon: HiOutlineCheckCircle, color: "success" },
              heading: t("search.table.row.delete.success", { count: rows.length }),
            });
          },
        },
      ),
    [id, deleteCompanies],
  );

  const tableConfig = useTableConfig({
    columns: columnValues,
    initialColumnOrder: initialColumnOrder,
    onColumnOrderChanged: onColumnOrderChange,
    //onRowOrderChanged: onRowOrderChange,
    onHiddenRowsChanged: onRemoveRows,
  });

  const processingOrEmptyComponent = useMemo(() => {
    if (initialLoading) {
      return null;
    }

    if (enrichProgress !== null) {
      return <CurrentlyEnriching percentage={enrichProgress} />;
    } else if (isFetched && flattenedData.length === 0 && !isFetching) {
      return query ? <NoListResults query={query} /> : <EmptyListDetail />;
    }

    return null;
  }, [initialLoading, enrichProgress, flattenedData.length, isFetched, query, isFetching]);

  const memoizedColumns = useMemo(
    () =>
      searchColumnsDef({
        t,
        listId: listDetail?.id,
        queryKey: [listDataQueryKey, { id: id!, query }],
        onShowPersonColumns: (val) => {
          tableConfig.addColumnsAfter(
            [CompanyListColumns.POINT_OF_CONTACT_EMAIL, CompanyListColumns.POINT_OF_CONTACT_LINKEDIN],
            CompanyListColumns.POINT_OF_CONTACT_FULL_NAME,
          );
          setShowPersonColumns(val);
        },
      }),
    [t, listDetail?.id, listDataQueryKey, id, query],
  );

  /** Invalidate the list data query when enrich progress is done */
  useEffect(() => {
    if (enrichProgress === null) {
      queryClient.invalidateQueries({
        queryKey: [listDataQueryKey, { id: id!, query }],
      });
    }
  }, [enrichProgress, id, query]);

  useEffect(() => {
    if (listDetail && listDetail.show_unmatched_items_notice && enrichProgress === null && unmatchedCount > 0) {
      showAlert({
        id: "unmatched-alert",
        size: "md",
        color: "warning",
        heading: t("unmatched.toast.title", { count: unmatchedCount }),
        isSelfClosing: false,
        children: (
          <div>
            <p>{t("unmatched.toast.description", { count: unmatchedCount })}</p>
            <UnmatchedCompaniesModalHandler
              listId={id!}
              count={unmatchedCount}
              alertIsShown={alertIsShown}
              btnProps={{
                size: "sm",
                trailingIcon: <HiOutlineArrowRight className={"size-4 stroke-2"} />,
                variants: "text",
                className: "!p-0 mt-1.5",
              }}
            />
          </div>
        ),
      });
    }

    return () => {
      toast.dismiss("unmatched-alert");
    };
  }, [listDetail, unmatchedCount, enrichProgress, id, alertIsShown]);

  if (listDetailHasError || listHasError) {
    return (
      <div className={"flex h-screen flex-grow flex-col overflow-y-auto overflow-x-hidden pl-6 pr-2 pt-5"}>
        <ListNotFound />
      </div>
    );
  }

  return (
    <div className={"flex h-screen flex-grow flex-col overflow-y-auto overflow-x-hidden pb-4 pl-6 pr-2 pt-5"}>
      <div className={"mb-2 flex items-start justify-between gap-1 pr-4"}>
        <div className={"flex items-center"}>
          <Button
            leadingIcon={<HiOutlineArrowSmallLeft className={"size-5"} />}
            onClick={() => history.back()}
            size={"xs"}
            className={"!py-0 !pl-1"}
            color={"natural"}
            variants={"text"}
          />
          <h2 className={"mt-1 flex items-center gap-2"}>
            {isLoadingList ? (
              <div className="h-7 w-20 animate-pulse rounded-full bg-gray-300" />
            ) : (
              <span className={"flex items-end space-x-2"}>
                <div className={"text-2xl font-semibold leading-none"}>{listDetail!.name}</div>
                <div className={"text-sm font-normal text-quaternary"}>
                  {Intl.NumberFormat().format(totalResults)} / {tierLimit}
                </div>
                {!isStaticList && (
                  <Badge
                    color={"primary"}
                    size={"sm"}
                    leadingIcon={<HiOutlineArrowPath />}
                    label={t("static.types.dynamic")}
                  />
                )}
                {enrichProgress !== null && <EnrichSpinner percentage={enrichProgress} />}
              </span>
            )}
          </h2>
        </div>
        <div className={"flex gap-2"}>
          {listDetail && (
            <>
              {enrichProgress === null && (
                <UnmatchedCompaniesModalHandler
                  listId={id!}
                  count={unmatchedCount}
                  alertIsShown={alertIsShown}
                  btnProps={{
                    size: "xs",
                    trailingIcon: <HiOutlineArrowRight className={"size-5"} />,
                    variants: "secondary",
                  }}
                />
              )}
              <CRMModalHandler listName={listDetail.name} listId={listDetail.id} />
              <ExportListButton
                listId={id!}
                listName={listDetail.name}
                visibleColumnIds={Object.entries(tableConfig.columnVisibility)
                  .filter(([, visible]) => visible)
                  .map(([id]) => id)}
              />
            </>
          )}
        </div>
      </div>

      <div className={"mb-6 flex items-end justify-between gap-3 pr-4"}>
        <div className={"max-w-56"}>
          <Input
            leftPlaceholder={<HiOutlineMagnifyingGlass className={"size-5"} />}
            sizing={"xs"}
            onChange={(e) => setDebounceQuery(e.target.value)}
            placeholder={t("static.search")}
          />
        </div>
        <div className={"flex items-center gap-3"}>
          <div>
            <CRMBadges listId={id!} />
          </div>

          <ConfigureTableColumnsButton columnNames={columnNames} columns={memoizedColumns} {...tableConfig} />
        </div>
      </div>

      {processingOrEmptyComponent ?? (
        <>
          <Table
            allColumns={memoizedColumns}
            data={flattenedData}
            hasContextMenu={isStaticList}
            canReorderColumns={true}
            canReorderRows={false}
            showHeaderWhileLoading
            loadingRowCount={loadingRowCount}
            isLoading={isLoadingListDetail || isTyping}
            loadMore={fetchNextPage}
            hasMoreData={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            {...tableConfig}
            tableOptions={{
              columnResizeDirection: "ltr",
              columnResizeMode: "onChange",
              getRowId: (row) => row.id,
              enableRowSelection: true,
            }}
            onDetailClicked={setSidePanelId}
            activeDetailId={sidePanelId || ""}
          />
          <CompanyDetailPanel id={sidePanelId} isOpen={Boolean(sidePanelId)} onClose={() => setSidePanelId(null)} />
        </>
      )}
    </div>
  );
};

export default ListDetail;

const EnrichSpinner = ({ percentage }: { percentage: number }) => {
  const { t } = useTranslation();

  return (
    <div className={"flex select-none items-center justify-center gap-2"}>
      <div>
        <Spinner size={"2xs"} />
      </div>
      <div className={"mt-1 text-xs text-brand-primary"}>
        {percentage}% {t("enrich.enriching")}
      </div>
    </div>
  );
};

const CurrentlyEnriching = ({ percentage }: { percentage: number }) => {
  const { t } = useTranslation();

  return (
    <FeaturedText
      heading={
        <>
          {percentage}% {t("enrich.enriching")}
        </>
      }
      body={t("enrich.enrichingText")}
      size={"lg"}
      featuredIcon={{
        icon: HiOutlineClock,
        color: "primary",
        size: "xl",
      }}
    />
  );
};
